import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

interface SessionModalProps {
  open: boolean;
  remaining: number; // Remaining ms before timeout
  onExtend: () => void;
  onLogout: () => void;
  isExtending?: boolean;
}

export default function SessionModal({
  open,
  remaining,
  onExtend,
  onLogout,
  isExtending = false,
}: SessionModalProps) {
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (open) {
      setCountdown(Math.ceil(remaining / 1000));
    }
  }, [open, remaining]);

  // Sync countdown with remaining
  useEffect(() => {
    if (!open) {return;}
    setCountdown(Math.ceil(remaining / 1000));
  }, [remaining, open]);

  const formatCountdown = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')} min`;
    }
    return `${secs} segundos`;
  };

  return (
    <Dialog
      open={open}
      onClose={onExtend}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          ⏳ Sesión por expirar
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
        <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
          Tu sesión está por expirar por inactividad.
        </Alert>

        <Box sx={{ my: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Tiempo restante:
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: countdown <= 10 ? 'error.main' : 'warning.main',
              animation: countdown <= 10 ? 'pulse 1s infinite' : 'none',
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          >
            {formatCountdown(countdown)}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          Haga clic en "Seguir conectado" para mantener la sesión activa.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          color="error"
          onClick={onLogout}
          disabled={isExtending}
          sx={{ minWidth: 120 }}
        >
          Cerrar sesión
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onExtend}
          disabled={isExtending}
          sx={{ minWidth: 140 }}
        >
          {isExtending ? (
            <CircularProgress size={20} sx={{ color: 'white' }} />
          ) : (
            'Seguir conectado'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
